/** @jsx jsx */
import { Flex, Text, jsx } from 'theme-ui'
import { shape, bool, string, arrayOf, number, func } from 'prop-types'
import { useEffect, useState, useContext } from 'react'
import {
  CheckboxGroupItem,
  PercentGroup,
  PercentGroupItem,
  SelectField,
  SelectFieldRange,
  TextArea,
  TextInput,
} from './FormInputs/index'
import { QuestionStepContext } from '../../context/QuestionStepContext'
import {
  IsQuestionRequiredError,
  ShowInvalidErrorMessage,
  QuestionHasTooManyAnswers,
} from '../common/form/InputValidationHelper'
import RadioInput from '../../components/common/form/FormInput/RadioInput'
import { checkIsOdd } from '../../utils/globalHelper'
import isEmpty from 'lodash.isempty'
import { getRelatedQuestions } from '../../utils/strategyBuilder'
import { verifyIfInputIsDisabled } from '../../components/common/form/FormikHelper'
import { any } from 'prop-types'
import { GetPercentGroupHeaders } from '../strategy-builder/helperFunctions'

const QuestionSectionContent = ({
  question,
  isStrategyBuilderQuestion,
  hideAllRequiredMessage,
  setFormValues,
  questionHasTooManyAnswers,
  setQuestionHasTooManyAnswers,
  hideTitle = false,
  values,
  setFieldValue,
  showQuestion,
  isMarketingMatrix,
  ...props
}) => {
  const {
    template,
    formikSchema,
    nextButtonClicked,
    percentCurrentError,
    setPercentCurrentError,
    percentDesiredError,
    setPercentDesiredError,
  } = useContext(QuestionStepContext)
  const {
    questions,
    currentStep,
    questionIsPercentGroupSection,
    questionIsMarketingRoleSection,
    questionIsMarketingGoals,
    questionIsBrandPositioning,
    questionIsTargetMarket,
    questionIsBusinessMix,
    firstPercentGroupComplete,
  } = template
  const currentQuestions = questions[currentStep]
  const relatedQuestions = getRelatedQuestions(currentQuestions)

  useEffect(() => {
    if (setQuestionHasTooManyAnswers) {
      const tooMany = QuestionHasTooManyAnswers(question, values)
      if (tooMany) {
        setQuestionHasTooManyAnswers(true)
      } else if (!tooMany) {
        setQuestionHasTooManyAnswers(false)
      }
    }
  }, [question, values])

  // only used in maintenance pkg
  useEffect(() => {
    if (typeof setFormValues === 'function') {
      setFormValues(values)
    }
  }, [values])
  const [percentGroupTotal, setPercentGroupTotal] = useState({
    current: 0,
    desired: 0,
  })

  const percentGroupHeaders = GetPercentGroupHeaders(question)
  const [isDropdownAndTargetMarket, setIsDropdownAndTargetMarket] = useState(
    false
  )

  const checkPercentTotal = values => {
    const newValues = Object.entries(values)
    let total = {
      current: 0,
      desired: 0,
    }
    newValues.forEach(keyValue => {
      const answer = question?.answers.find(answer => answer.id === keyValue[0])
      if (answer) {
        // if first group is complete, check even answers. Otherwise, check odd.
        const isOdd = checkIsOdd(answer.id)
        const answerIndex = isOdd ? 'current' : 'desired'
        const value = keyValue[1]
        const formattedValue = parseInt(value.substring(0, value.length))
        total[answerIndex] += formattedValue
        if (isOdd) setPercentCurrentError(total[answerIndex] !== 100)
        else setPercentDesiredError(total[answerIndex] !== 100)
      }
    })

    setPercentGroupTotal(total)
  }

  const verifyCheckboxQuantity = () => {
    // Verify that the number of checkboxes selected is equal to the number of checkboxes in the question
    // if not verify if it's a checkbox with textarea
    const types = question?.answers?.map(({ answerType }) => answerType)
    if (!isEmpty(types) && types.includes('checkbox')) {
      return types.every((type, i) => {
        if (type === 'checkbox') return true
        return type === 'textarea' && types[i - 1] === 'checkbox'
      })
    }
  }

  const getContainerStyle = () => {
    const getMarginTop = () => {
      if (questionIsTargetMarket || questionIsMarketingRoleSection) return 0
      return 3
    }

    const getMarginBottom = () => {
      if (
        questionIsTargetMarket ||
        questionIsPercentGroupSection ||
        questionIsMarketingRoleSection
      )
        return 0
      return 5
    }

    let baseStyle = {
      flexDirection: 'column',
      width: isDropdownAndTargetMarket ? 'auto' : '100%',
      ml: isMarketingMatrix ? 0 : isStrategyBuilderQuestion ? 0 : 5,
      mt: getMarginTop(),
      mb: getMarginBottom(),
      '&> *': !questionIsTargetMarket && {
        mb:
          questionIsPercentGroupSection || questionIsMarketingRoleSection
            ? 0
            : 5,
      },
    }

    const allAnswersAreCheckbox = verifyCheckboxQuantity()
    if (questionIsTargetMarket) baseStyle = { ...baseStyle, gap: '20px' }
    if (!allAnswersAreCheckbox) return baseStyle
    return {
      ...baseStyle,
      display: 'grid',
      gridTemplateColumns: questionIsTargetMarket ? '1fr 1fr 1fr' : '1fr 1fr',
    }
  }

  useEffect(() => {
    if (questionIsPercentGroupSection) {
      checkPercentTotal(values)
    }
  }, [values, questionIsPercentGroupSection, firstPercentGroupComplete])

  useEffect(() => {
    setIsDropdownAndTargetMarket(
      question?.answers[0].answerType === 'dropdown' && questionIsTargetMarket
    )
  }, [question, questionIsTargetMarket])

  const textFieldIsNotWriteIn = previousAnswerType => {
    return previousAnswerType !== 'radio' && previousAnswerType !== 'checkbox'
  }

  const changeIsHide = ({ id, state = false }) => {
    const ID = `${id}-isHide`
    if (!formikSchema?._nodes.includes(ID)) return
    setFieldValue(ID, state)
  }

  const shouldDisplayWriteInTextField = (
    previousAnswerId,
    values,
    questionId
  ) => {
    return Array.isArray(values[questionId])
      ? values[questionId]?.includes(previousAnswerId)
      : values[questionId] === previousAnswerId
  }

  const setPercentInputsState = () => {
    const questionsFiltered = question?.answers?.filter(
      question =>
        !question.text.toLowerCase().includes('desired') &&
        !isEmpty(question.answerValue)
    )
    // set the odd questions to be hidden by default and update it based in the user answers
    question?.answers?.forEach(({ answerType, id }) => {
      if (answerType === 'PercentInput') {
        if (checkIsOdd(id)) {
          if (!percentCurrentError) {
            changeIsHide({ id, state: true })
            if (!percentDesiredError) {
              changeIsHide({ id, state: true })
            } else {
              changeIsHide({ id })
            }
          } else {
            changeIsHide({ id })
          }
        } else if (questionsFiltered.length > 0) {
          changeIsHide({ id })
        } else {
          changeIsHide({ id, state: true })
        }
      }
    })
  }

  const [showRequiredError, setRequiredError] = useState(false)
  const [invalidError, setInvalidError] = useState(null)

  useEffect(() => {
    if (nextButtonClicked) {
      setRequiredError(
        IsQuestionRequiredError(question, values, questionIsPercentGroupSection)
      )
      setInvalidError(ShowInvalidErrorMessage(question, values))
    }
  }, [nextButtonClicked, values, question])

  useEffect(() => {
    if (!isEmpty(question.hideTrigger)) {
      changeIsHide({ id: question.id, state: !showQuestion })
    }
  }, [showQuestion, formikSchema])

  useEffect(() => {
    if (questionIsPercentGroupSection) setPercentInputsState()
  }, [
    percentCurrentError,
    percentDesiredError,
    questionIsPercentGroupSection,
    question,
    values,
  ])

  return (
    <Flex
      as="section"
      sx={{
        flexDirection: 'column',
        mx: [3, 6],
        mb: isStrategyBuilderQuestion && !questionIsPercentGroupSection ? 0 : 6,
        mt: questionIsTargetMarket ? 7 : questionIsMarketingRoleSection ? 0 : 5,
        width: questionIsTargetMarket ? 'auto' : '100%',
        minWidth: questionIsMarketingRoleSection ? '100%' : 0,
        gap: questionIsTargetMarket && '10px',
      }}
      {...props}
    >
      {!hideTitle && (
        <Text
          data-automation-id={`question-section-question-text-${question.id}`}
          variant={
            // marketing objectives has different typography styling than other sections
            isStrategyBuilderQuestion &&
            !questionIsPercentGroupSection &&
            (!question.templateId === '3' || question.sectionId === '9')
              ? 'mediumText'
              : 'h6'
          }
          sx={{
            fontWeight: 'normal',
            color: 'black',
            fontSize: questionIsMarketingGoals ? 5 : 3,
            mb: !isDropdownAndTargetMarket && 12,
          }}
        >
          {question.text}
        </Text>
      )}
      {showRequiredError && !hideAllRequiredMessage && (
        <Flex
          sx={{
            width: '40%',
            justifyContent: 'flex-start',
          }}
        >
          <Text variant="mediumText" color="tomato">
            Please fill in your response
          </Text>
        </Flex>
      )}
      {question.subText?.length > 0 && (
        <Text
          data-automation-id={`question-section-question-subText-${question.id}`}
          variant={
            // marketing objectives has different typography styling than other sections
            isStrategyBuilderQuestion &&
            !questionIsPercentGroupSection &&
            (!question.templateId === '3' || question.sectionId === '9')
              ? 'smallText'
              : 'mediumText'
          }
          sx={{
            fontWeight: 'normal',

            color:
              questionHasTooManyAnswers && nextButtonClicked ? 'red' : 'black',
            mb: 4,
          }}
          className="subText"
        >
          {question.subText}
        </Text>
      )}
      {questionIsPercentGroupSection && (
        <Flex
          sx={{
            width: '80%',
            justifyContent: 'flex-end',
            mt: 5,
          }}
        >
          <Flex
            sx={{
              justifyContent: 'space-between',
              width: '30%',
              pb: 1,
            }}
          >
            <Text
              data-automation-id="question-section-percentGroupHeaders-0"
              variant="largeText"
              color="black"
            >
              {percentGroupHeaders[0]}
            </Text>
            {firstPercentGroupComplete && (
              <Text
                data-automation-id="question-section-percentGroupHeaders-1"
                variant="largeText"
              >
                {percentGroupHeaders[1]}
              </Text>
            )}
          </Flex>
        </Flex>
      )}
      <Flex sx={getContainerStyle()} {...props}>
        {question?.answers[0].answerType.includes('dropdown') ? (
          question?.answers[0].answerType === 'dropdownGroup' ? (
            <SelectFieldRange
              question={question}
              selectedValues={values[question.id]}
            />
          ) : (
            <SelectField
              id={question.id}
              label={question.id}
              name={question.id}
              hideLabel
              placeholder={question.answers[0].placeholder}
              value={values[question.id]}
              removeMargin={isStrategyBuilderQuestion || questionIsTargetMarket}
              sx={{
                color: 'black',
                margin: questionIsTargetMarket && 0,
                ':disabled': questionIsTargetMarket && {
                  cursor: 'not-allowed',
                },
              }}
              containerSX={{
                margin: questionIsTargetMarket && 0,
              }}
              disabled={verifyIfInputIsDisabled({
                relatedQuestions,
                question,
                values,
              })}
            >
              {question.answers.map((answer, i) => (
                <option
                  data-automation-id="question-section-answer-dropdownGroup-type"
                  key={answer.id}
                  value={i > 0 ? answer.id : ''}
                >
                  {i > 0 ? answer.text : answer.placeHolder}
                </option>
              ))}
            </SelectField>
          )
        ) : (
          question?.answers.map((option, answerIndex) => (
            <Flex key={option.id}>
              {option.answerType === 'textarea' && (
                <Flex
                  sx={{
                    mb: !questionIsTargetMarket && -48,
                    mt: 0,
                    width: '100%',
                    alignItems: questionIsBusinessMix && 'center',
                  }}
                >
                  {question?.answers[answerIndex - 1]?.answerType ===
                    'checkbox' && (
                    <CheckboxGroupItem
                      value={question?.answers[answerIndex - 1].id.toString()}
                      label={question?.answers[answerIndex - 1].text}
                      list={values[question.id]}
                      groupId={question.id.toString()}
                      sx={{
                        width: questionIsBusinessMix && '20%',
                      }}
                    />
                  )}
                  {(textFieldIsNotWriteIn(
                    question?.answers[answerIndex - 1]?.answerType
                  ) ||
                    shouldDisplayWriteInTextField(
                      question?.answers[answerIndex - 1]?.id,
                      values,
                      option.questionId
                    )) && (
                    <TextArea
                      data-automation-id="question-section-answer-textarea-type"
                      value={
                        textFieldIsNotWriteIn(
                          question?.answers[answerIndex - 1]?.answerType
                        )
                          ? values[option.questionId] || ''
                          : values[option.id] || ''
                      }
                      label={option.text}
                      name={
                        textFieldIsNotWriteIn(
                          question?.answers[answerIndex - 1]?.answerType
                        )
                          ? option.questionId
                          : option.id
                      }
                      placeholder={option.placeHolder}
                      rows="4"
                      showCount={
                        question?.validation?.maxAllowedCharacter != null
                      }
                      maxLength={
                        question?.validation?.maxAllowedCharacter || 1022
                      }
                      sx={
                        isStrategyBuilderQuestion &&
                        question?.answers[answerIndex - 1]?.answerType ===
                          'checkbox' && {
                          height: '45px',
                          marginBottom: 0,
                          ':disabled': {
                            cursor: 'not-allowed',
                          },
                        }
                      }
                      containerProps={{
                        marginBottom:
                          isStrategyBuilderQuestion && '0 !important',
                        label: questionIsBrandPositioning && {
                          fontSize: 3,
                          fontWeight: 'normal',
                          color: 'darkGray',
                        },
                      }}
                      disabled={verifyIfInputIsDisabled({
                        relatedQuestions,
                        question,
                        values,
                      })}
                    />
                  )}
                </Flex>
              )}
              {option.answerType === 'date' && (
                <TextInput
                  data-automation-id="question-section-answer-date-type"
                  name={option.questionId}
                  type="date"
                  value={values[option.questionId] || ''}
                  label="" //required by TextInput definition
                />
              )}
              {(option.answerType === 'text' ||
                option.answerType === 'number') &&
                (textFieldIsNotWriteIn(
                  question?.answers[answerIndex - 1]?.answerType
                ) ||
                  shouldDisplayWriteInTextField(
                    question?.answers[answerIndex - 1]?.id,
                    values,
                    option.questionId
                  )) && (
                  <Flex
                    sx={{
                      mb: questionIsTargetMarket
                        ? 0
                        : textFieldIsNotWriteIn(
                            question?.answers[answerIndex - 1]?.answerType
                          )
                        ? 4
                        : -32,
                      mt: questionIsTargetMarket
                        ? 0
                        : textFieldIsNotWriteIn(
                            question?.answers[answerIndex - 1]?.answerType
                          )
                        ? 0
                        : -2,
                      ml: questionIsTargetMarket
                        ? 0
                        : textFieldIsNotWriteIn(
                            question?.answers[answerIndex - 1]?.answerType
                          )
                        ? 0
                        : 6,
                    }}
                  >
                    <TextInput
                      data-automation-id="question-section-answer-text-type"
                      value={
                        textFieldIsNotWriteIn(
                          question?.answers[answerIndex - 1]?.answerType
                        )
                          ? values[option.questionId] || ''
                          : values[option.id] || ''
                      }
                      label={option.text}
                      name={
                        textFieldIsNotWriteIn(
                          question?.answers[answerIndex - 1]?.answerType
                        )
                          ? option.questionId
                          : option.id
                      }
                      placeholder={option.placeHolder}
                      inputWidth={
                        !questionIsMarketingRoleSection
                          ? '397px'
                          : question.order % 2
                          ? '397px'
                          : '323px'
                      }
                      maxLength={
                        question?.validation?.maxAllowedCharacter || 1022
                      }
                      removeMargin={
                        textFieldIsNotWriteIn(
                          question?.answers[answerIndex - 1]?.answerType
                        ) || questionIsTargetMarket
                      }
                      errorMessage={invalidError}
                      sx={{
                        maxWidth: questionIsTargetMarket && '80px',
                        textAlign:
                          questionIsTargetMarket &&
                          option.answerType === 'number' &&
                          'center',
                      }}
                      disabled={verifyIfInputIsDisabled({
                        relatedQuestions,
                        question,
                        values,
                      })}
                      type={option.answerType === 'number' ? 'number' : 'text'}
                      min={option.answerType === 'number' ? 0 : undefined}
                      onKeyPress={event => {
                        if (
                          option.answerType === 'number' &&
                          !/[0-9]/.test(event.key)
                        ) {
                          event.preventDefault()
                        }
                      }}
                    />
                  </Flex>
                )}
              {option.answerType === 'checkbox' &&
                question?.answers[answerIndex + 1]?.answerType !==
                  'textarea' && (
                  <CheckboxGroupItem
                    value={option.id.toString()}
                    label={option.text}
                    list={values[question.id]}
                    groupId={question.id.toString()}
                  />
                )}
              {option.answerType === 'radio' && (
                <RadioInput
                  values={values}
                  question={question}
                  option={option}
                />
              )}
              {option.answerType === 'PercentInput' &&
                option.order % 2 > 0 &&
                (questionIsPercentGroupSection ? (
                  <PercentGroup
                    answerSet={[option, question.answers[option.order]]}
                    selectedValues={values}
                    firstGroupComplete={firstPercentGroupComplete}
                  />
                ) : (
                  <Flex
                    sx={{
                      flexDirection: 'column',
                      width: '25%',
                      mb: !questionIsTargetMarket && -64,
                    }}
                  >
                    <Flex
                      sx={{
                        justifyContent: 'space-between',
                        py: 1,
                      }}
                    >
                      <Text variant="largeText" color={'black'}>
                        {option.text}
                      </Text>
                      {question.answers[0].groupId && (
                        <Text variant="largeText" color={'black'}>
                          {question.answers[option.order].text}
                        </Text>
                      )}
                    </Flex>
                    <Flex
                      sx={{
                        justifyContent: 'space-between',
                        pb: 1,
                      }}
                    >
                      <PercentGroupItem
                        answer={option}
                        value={values[option.id]}
                        isAllowed={({ floatValue }) =>
                          floatValue <= 100 && floatValue >= 0
                        }
                      />
                      {question.answers[0].groupId && (
                        <PercentGroupItem
                          answer={question.answers[option.order]}
                          value={values[question.answers[option.order].id]}
                          isAllowed={({ floatValue }) =>
                            floatValue <= 100 && floatValue >= 0
                          }
                        />
                      )}
                    </Flex>
                  </Flex>
                ))}
            </Flex>
          ))
        )}
        {questionIsPercentGroupSection && (
          <Flex
            sx={{
              flexDirection: 'column',
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
              width: '80%',
            }}
          >
            <Flex
              sx={{
                alignItems: 'center',
                gap: '50px',
              }}
            >
              <Flex
                sx={{
                  flexDirection: 'column',
                }}
              >
                <Flex
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text
                    variant="allCaps"
                    sx={{
                      color: 'forest',
                      marginRight: 5,
                    }}
                  >
                    Total
                  </Text>
                  <Flex
                    sx={{
                      justifyContent: 'center',
                      width: '83px',
                      borderBottom: '1px solid #304A3D',
                    }}
                  >
                    <Text
                      variant="h5"
                      as="h5"
                      color="primary"
                      sx={{ fontFamily: 'Graphik', lineHeight: '35px' }}
                      data-automation-id="question-section-total-percent-current"
                    >
                      {percentGroupTotal['current']}%
                    </Text>
                  </Flex>
                </Flex>
                <Flex
                  sx={{
                    justifyContent: 'flex-end',
                    mt: 1,
                  }}
                >
                  <Text
                    variant="smallText"
                    //isEmpty or currentError
                    color={percentCurrentError ? 'tomato' : 'darkGray'}
                  >
                    *Must add
                    <br /> up to 100%
                  </Text>
                </Flex>
              </Flex>

              <Flex
                sx={{
                  flexDirection: 'column',
                  pointerEvents: firstPercentGroupComplete ? 'none' : 'all',
                  opacity: firstPercentGroupComplete ? 1 : 0,
                }}
              >
                <Flex
                  sx={{
                    justifyContent: 'center',
                    width: '83px',
                    borderBottom: '1px solid #304A3D',
                  }}
                >
                  <Text
                    variant="h5"
                    as="h5"
                    color="primary"
                    sx={{ fontFamily: 'Graphik', lineHeight: '35px' }}
                    data-automation-id="question-section-total-percent-desired"
                  >
                    {percentGroupTotal['desired']}%
                  </Text>
                </Flex>
                <Flex
                  sx={{
                    justifyContent: 'flex-end',
                    mt: 1,
                  }}
                >
                  <Text
                    variant="smallText"
                    color={percentDesiredError ? 'tomato' : 'darkGray'}
                  >
                    *Must add
                    <br /> up to 100%
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}

QuestionSectionContent.propTypes = {
  question: shape({
    answers: arrayOf(
      shape({
        answerType: string,
        id: string,
        order: number,
        // when empty placeHolder is null
        placeHolder: string,
        text: string,
      })
    ),
    id: string,
    subText: string,
    text: string,
  }).isRequired,
  isMarketingMatrix: bool,
  setQuestionHasTooManyAnswers: func,
  questionHasTooManyAnswers: bool,
  isStrategyBuilderQuestion: bool,
  hideAllRequiredMessage: bool,
  setFormValues: func,
  hideTitle: bool,
  values: any,
  showQuestion: bool,
  setFieldValue: any,
}

export default QuestionSectionContent
